/**
 * Upload Service - TUS Protocol Implementation for File Uploads
 * Handles chunked, resumable uploads with progress tracking
 *
 * TASK-083: Refactored to use httpClient for gateway enforcement
 * All requests now go through API gateway with JWT authentication
 */

import { httpClient, httpClientUpload } from '../lib/httpClient';
import { API_CONFIG } from '../config/api-config';

export interface UploadProgress {
  uploadId: string;
  bytesUploaded: number;
  bytesTotal: number;
  progress: number; // 0-100
  status: 'uploading' | 'processing' | 'ready' | 'complete' | 'error';
  error?: string;
}

export interface UploadInitResponse {
  upload_id: string;
  tus_resumable_uri: string;
}

export interface PreviewData {
  upload_id: string;
  total_rows: number;
  sample_rows: Array<Record<string, any>>;
  auto_mappings: Record<string, string>;
  unmapped_fields: string[];
  validation_errors: Array<{ row: number; field: string; error: string; severity: string }>;
  error_percentage: number;
  detected_template_type: string;
  template_confidence: number;
  filename?: string;
  // PDF-specific fields
  extraction_method?: string;
  extraction_confidence?: number;
  total_pages?: number;
  page_extractions?: Array<{
    page_number: number;
    extraction_type: string;
    extraction_method: string;
    raw_text?: string;
    structured_data?: any[];
    confidence_score: number;
    page_width?: number;
    page_height?: number;
  }>;
  metadata?: {
    document_type?: string;
    page_count?: number;
    has_text?: boolean;
    has_tables?: boolean;
    extraction_method?: string;
  };
  summary?: {
    total_rows?: number;
    detected_tables?: number;
    extraction_confidence?: number;
  };
  pdf_metadata?: {
    page_count?: number;
    document_type?: string;
    has_tables?: boolean;
    detected_bank?: string;
  };
  // TASK-225: Multi-schema extraction results
  kyc_data?: Record<string, any>;
  risk_profile_data?: Record<string, any>;
  staging_id?: string;
  schemas_extracted?: string[];
}

class UploadService {
  private wsBaseUrl: string;
  private chunkSize: number = 10 * 1024 * 1024; // 10MB chunks

  constructor() {
    // ✅ TASK-083: Use gateway WebSocket for account isolation and authentication
    // Gateway now has WebSocket proxy at /ws/uploads/{upload_id}
    this.wsBaseUrl = API_CONFIG.WS_GATEWAY_URL;
  }

  /**
   * Initialize TUS upload session
   * TASK-083: Uses httpClient for gateway enforcement
   */
  async initializeUpload(
    file: File,
    clientId: string = 'default-client',
    templateType?: string
  ): Promise<UploadInitResponse> {
    try {
      const data = await httpClient.post<UploadInitResponse>('/uploads/initialize', {
        filename: file.name,
        file_size: file.size,
        file_type: file.type,
        client_id: clientId,
        template_type: templateType,
      });

      return data;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail || error?.message || 'Upload initialization failed';
      throw new Error(`Upload initialization failed: ${errorMessage}`);
    }
  }

  /**
   * Upload file with TUS protocol - chunked and resumable
   * TASK-083: Uses httpClientUpload for gateway enforcement
   */
  async uploadFile(
    file: File,
    onProgress: (progress: UploadProgress) => void,
    clientId: string
  ): Promise<string> {
    // Initialize upload session
    const initResponse = await this.initializeUpload(file, clientId);
    const uploadId = initResponse.upload_id;

    // Upload file in chunks
    let uploadedBytes = 0;

    while (uploadedBytes < file.size) {
      const start = uploadedBytes;
      const end = Math.min(start + this.chunkSize, file.size);
      const chunk = file.slice(start, end);

      try {
        const chunkArrayBuffer = await chunk.arrayBuffer();

        // Upload chunk via gateway
        const rawClient = httpClientUpload.getRawClient();
        const response = await rawClient.patch(`/uploads/${uploadId}`, chunkArrayBuffer, {
          headers: {
            'Upload-Offset': uploadedBytes.toString(),
            'Content-Type': 'application/offset+octet-stream',
            'Content-Length': chunk.size.toString(),
            'Tus-Resumable': '1.0.0',
          },
        });

        // Update progress
        const newOffset = response.headers['upload-offset'];
        if (newOffset) {
          uploadedBytes = parseInt(newOffset);
        } else {
          // If no offset header, assume the chunk was uploaded successfully
          uploadedBytes = end;
        }
        const progress = Math.round((uploadedBytes / file.size) * 100);

        onProgress({
          uploadId,
          bytesUploaded: uploadedBytes,
          bytesTotal: file.size,
          progress,
          status: uploadedBytes >= file.size ? 'processing' : 'uploading',
        });

      } catch (error) {
        onProgress({
          uploadId,
          bytesUploaded: uploadedBytes,
          bytesTotal: file.size,
          progress: Math.round((uploadedBytes / file.size) * 100),
          status: 'error',
          error: error instanceof Error ? error.message : 'Upload failed',
        });
        throw error;
      }
    }

    return uploadId;
  }

  /**
   * Get data preview with schema mapping
   * TASK-083: Uses httpClient for gateway enforcement
   */
  async getPreview(uploadId: string): Promise<PreviewData> {
    return httpClient.get<PreviewData>(`/uploads/${uploadId}/preview`);
  }

  /**
   * Update field mappings
   * TASK-083: Uses httpClient for gateway enforcement
   */
  async updateMapping(
    uploadId: string,
    userMappings: Record<string, string>,
    templateTypeOverride?: string
  ): Promise<void> {
    await httpClient.post(`/uploads/${uploadId}/mapping`, {
      user_mappings: userMappings,
      template_type_override: templateTypeOverride,
    });
  }

  /**
   * Confirm upload and start final ingestion
   * TASK-083: Uses httpClient for gateway enforcement
   */
  async confirmUpload(uploadId: string): Promise<void> {
    await httpClient.post(`/uploads/${uploadId}/confirm`);
  }

  /**
   * Start ingestion process (alias for confirmUpload for PDF preview modal)
   */
  async startIngestion(uploadId: string): Promise<void> {
    return this.confirmUpload(uploadId);
  }

  /**
   * Create WebSocket connection for real-time progress updates
   */
  createProgressWebSocket(
    uploadId: string,
    onMessage: (data: any) => void,
    onError: (error: Event) => void
  ): WebSocket {
    const wsUrl = `${this.wsBaseUrl}/ws/uploads/${uploadId}`;
    console.log('Creating WebSocket connection to:', wsUrl);
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log('Upload WebSocket connected:', uploadId);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('WebSocket message received:', data);
        onMessage(data);
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('Upload WebSocket error:', error);
      onError(error);
    };

    ws.onclose = (event) => {
      console.log('Upload WebSocket closed:', event.code, event.reason);
    };

    return ws;
  }

  /**
   * Handle drag and drop file upload
   */
  handleDrop = async (
    event: React.DragEvent<HTMLElement>,
    onProgress: (progress: UploadProgress) => void,
    clientId: string,
    allowedTypes: string[] = ['.xlsx', '.xls', '.csv', '.pdf'],
    maxSize: number = 1024 * 1024 * 1024 // 1GB
  ): Promise<string | null> => {
    event.preventDefault();
    const files = event.dataTransfer.files;

    if (files.length === 0) {
      throw new Error('No files dropped');
    }

    const file = files[0];

    // Validate file type
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!allowedTypes.includes(fileExtension)) {
      throw new Error(`File type ${fileExtension} not supported. Allowed types: ${allowedTypes.join(', ')}`);
    }

    // Validate file size
    if (file.size > maxSize) {
      throw new Error(`File too large (${Math.round(file.size / (1024 * 1024))}MB). Maximum size: ${Math.round(maxSize / (1024 * 1024))}MB`);
    }

    return this.uploadFile(file, onProgress, clientId);
  };

  /**
   * Handle file input upload (single file - for backward compatibility)
   */
  handleFileInput = async (
    files: FileList | null,
    onProgress: (progress: UploadProgress) => void,
    clientId: string,
    allowedTypes: string[] = ['.xlsx', '.xls', '.csv', '.pdf'],
    maxSize: number = 1024 * 1024 * 1024 // 1GB
  ): Promise<string | null> => {
    if (!files || files.length === 0) {
      return null;
    }

    const file = files[0];

    // Validate file type
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!allowedTypes.includes(fileExtension)) {
      throw new Error(`File type ${fileExtension} not supported. Allowed types: ${allowedTypes.join(', ')}`);
    }

    // Validate file size
    if (file.size > maxSize) {
      throw new Error(`File too large (${Math.round(file.size / (1024 * 1024))}MB). Maximum size: ${Math.round(maxSize / (1024 * 1024))}MB`);
    }

    return this.uploadFile(file, onProgress, clientId);
  };

  /**
   * Handle multiple file uploads
   */
  handleMultipleFileInput = async (
    files: FileList | null,
    onProgress: (uploadId: string, progress: UploadProgress) => void,
    clientId: string,
    allowedTypes: string[] = ['.xlsx', '.xls', '.csv', '.pdf'],
    maxSize: number = 1024 * 1024 * 1024 // 1GB
  ): Promise<string[]> => {
    if (!files || files.length === 0) {
      return [];
    }

    const uploadIds: string[] = [];

    // Process each file
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Validate file type
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!allowedTypes.includes(fileExtension)) {
        onProgress('', {
          uploadId: '',
          bytesUploaded: 0,
          bytesTotal: file.size,
          progress: 0,
          status: 'error',
          error: `File ${file.name}: type ${fileExtension} not supported. Allowed types: ${allowedTypes.join(', ')}`
        });
        continue;
      }

      // Validate file size
      if (file.size > maxSize) {
        onProgress('', {
          uploadId: '',
          bytesUploaded: 0,
          bytesTotal: file.size,
          progress: 0,
          status: 'error',
          error: `File ${file.name} too large (${Math.round(file.size / (1024 * 1024))}MB). Maximum size: ${Math.round(maxSize / (1024 * 1024))}MB`
        });
        continue;
      }

      try {
        const uploadId = await this.uploadFile(file, (progress) => onProgress(progress.uploadId, progress), clientId);
        if (uploadId) {
          uploadIds.push(uploadId);
        }
      } catch (error) {
        onProgress('', {
          uploadId: '',
          bytesUploaded: 0,
          bytesTotal: file.size,
          progress: 0,
          status: 'error',
          error: `File ${file.name}: ${error instanceof Error ? error.message : 'Upload failed'}`
        });
      }
    }

    return uploadIds;
  };

  /**
   * Get all uploads for a client
   * TASK-083: Uses httpClient for gateway enforcement
   */
  async getAllUploads(clientId: string = 'default-client'): Promise<any[]> {
    return httpClient.get<any[]>(`/uploads/list?client_id=${clientId}`);
  }

  /**
   * Get upload status
   * TASK-083: Uses httpClient for gateway enforcement
   */
  async getUploadStatus(uploadId: string): Promise<any> {
    return httpClient.get<any>(`/uploads/${uploadId}/status`);
  }

  /**
   * Get pending reviews for a client
   * TASK-191: Fetches all extraction staging records awaiting review
   */
  async getPendingReviews(clientId: string): Promise<any> {
    return httpClient.get<any>(`/clients/${clientId}/pending-reviews`);
  }

  /**
   * Approve staging extraction and create KYC profile
   * TASK-214: Calls staging approval endpoint to save KYC data
   */
  async approveStagingExtraction(stagingId: string, approvedData?: any): Promise<any> {
    return httpClient.post<any>(`/api/clients/kyc/staging/${stagingId}/approve`, approvedData || {});
  }
}

// Export singleton instance
export const uploadService = new UploadService();
export default uploadService;